import React from "react";
import {
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  FormControl,
} from "@mui/material";
import { observer } from "mobx-react";
import {
  VisibilityIcon as Visibility,
  VisibilityOffIcon as VisibilityOff,
} from "../../icons";
import { useRootStore } from "../../store/contexts";
import { Link } from "react-router";

type LoginFormProps = {
  embedded?: boolean;
  onLoginSuccess?: () => void;
  redirectUrl?: string;
  title?: string;
};

export const LoginForm = observer(
  ({
    embedded = false,
    onLoginSuccess,
    redirectUrl,
    title,
  }: LoginFormProps = {}) => {
    const rootStore = useRootStore();

    const paperStyle = {
      padding: "29px 20px",
      height: "auto",
      width: 320,
      margin: "20px auto",
    };
    const logoStyle = {
      maxWidth: "88%",
      height: "auto",
      objectFit: "contain" as const,
      marginTop: 12,
      marginBottom: 12,
    };

    const auth = rootStore.authStore;
    const lang = rootStore.langStore;

    const handleLogInClick = async () => {
      if (embedded) {
        await auth.loginInline();
      } else {
        await auth.login();
      }
      if (auth.status === "logged") {
        if (onLoginSuccess) {
          onLoginSuccess();
        } else if (redirectUrl) {
          rootStore.router.goTo(redirectUrl);
        }
      }
    };

    const handleChangeEmail = (event: any) => {
      auth.setEmail(event.target.value);
    };

    const handleChangePassword = (event: any) => {
      auth.setPassword(event.target.value);
    };

    const handleClickShowPassword = () => {
      auth.toggleShowPassword();
    };

    const handleMouseDownPassword = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
    };

    const formContent = (
      <FormControl fullWidth={embedded}>
        <TextField
          autoComplete={"email"}
          type={"email"}
          name={"email"}
          id={"email"}
          sx={{ mx: 1, my: 1.5 }}
          label="Email"
          onChange={handleChangeEmail}
          placeholder="Enter username"
          fullWidth
          required
        >
          {auth.email}
        </TextField>

        <TextField
          autoComplete={"current-password"}
          name={"password"}
          id={"password"}
          sx={{ mx: 1, my: 1.5 }}
          label={lang.passwordLabel}
          onChange={handleChangePassword}
          placeholder="Enter password"
          type={auth.showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position={"end"}>
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {auth.showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          required
        >
          {auth.password}
        </TextField>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{ mx: 1, my: 1.5 }}
          fullWidth
          onClick={handleLogInClick}
        >
          {lang.logInLabel}
        </Button>
        <Typography color="error">
          {auth.wrongAudienceOrigin ? (
            <>
              {lang.wrongAudienceParts[0]}
              <a href={`${auth.wrongAudienceOrigin}/login`}>
                {new URL(auth.wrongAudienceOrigin).host}
              </a>
              {lang.wrongAudienceParts[1]}
            </>
          ) : (
            auth.errorMessage
          )}
        </Typography>
        <Link
          to="/forgot-password"
          style={{
            display: "block",
            marginTop: 23,
            textAlign: "center",
            color: "#17405C",
            textDecoration: "none",
          }}
        >
          {lang.forgotPasswordLinkLabel}
        </Link>
      </FormControl>
    );

    if (embedded) {
      return formContent;
    }

    return (
      <Grid
        sx={{
          minHeight: "100vh",
          backgroundColor: "#17405C",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper elevation={10} style={paperStyle}>
          <Grid container direction="column" alignItems="center">
            <img src="/logo1.png" alt={lang.logInLabel} style={logoStyle} />
          </Grid>
          {title && (
            <Typography variant="h6" component="h1" align="center">
              {title}
            </Typography>
          )}
          {formContent}
        </Paper>
      </Grid>
    );
  }
);
